/**
 * useAuth – Zustand-backed authentication store.
 * Real authentication: calls /api/auth/login and /api/auth/verify-mfa.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

interface UserPermissions {
  can_trade: boolean
  can_approve: boolean
  can_configure: boolean
  can_view_all: boolean
}

interface User {
  id: string
  name: string
  email: string
  role: string
  company: string
  title: string
  avatar?: string
  permissions?: UserPermissions
  must_change_password?: boolean
  is_platform_admin?: boolean
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  mfaToken: string | null
  accessToken: string | null
  // Actions
  login: (email: string, password: string) => Promise<void>
  verifyMFA: (code: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  logout: () => void
  clearError: () => void
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      mfaToken: null,
      accessToken: null,

      login: async (email: string, password: string) => {
        set({ loading: true, error: null })
        try {
          const { data } = await api.post('/api/auth/login', { email, password })

          if (data.requires_mfa) {
            // Store the temporary MFA token — user info comes after MFA verify
            set({
              mfaToken: data.mfa_token,
              user: { id: '', name: email.split('@')[0], email, role: '', company: '', title: '' },
              loading: false,
            })
          }
        } catch (err: any) {
          const message =
            err?.response?.data?.detail || 'Login failed. Please try again.'
          set({ error: message, loading: false })
          throw new Error(message)
        }
      },

      verifyMFA: async (code: string) => {
        const { mfaToken } = get()
        if (!mfaToken) {
          set({ error: 'Session expired. Please log in again.', mfaToken: null, user: null, isAuthenticated: false })
          throw new Error('Must login first')
        }
        set({ loading: true, error: null })
        try {
          const { data } = await api.post('/api/auth/verify-mfa', {
            mfa_token: mfaToken,
            totp_code: code,
          })

          const user: User = {
            id: data.user.user_id,
            name: data.user.name,
            email: data.user.email,
            role: data.user.role,
            company: data.user.company,
            title: data.user.title,
            permissions: data.user.permissions,
            must_change_password: data.user.must_change_password ?? false,
            is_platform_admin: data.user.is_platform_admin ?? false,
          }

          // Store the JWT access token
          localStorage.setItem('av-access-token', data.access_token)

          set({
            user,
            accessToken: data.access_token,
            isAuthenticated: true,
            mfaToken: null,
            loading: false,
          })
        } catch (err: any) {
          const detail = err?.response?.data?.detail || ''
          const isExpired = detail.includes('expired') || detail.includes('invalid') || err?.response?.status === 401
          const message = isExpired
            ? 'Session expired. Please log in again.'
            : detail || 'MFA verification failed. Please try again.'
          set({
            error: message,
            loading: false,
            ...(isExpired ? { mfaToken: null, user: null } : {}),
          })
          throw new Error(message)
        }
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        const { accessToken, user } = get()
        if (!accessToken || !user) {
          throw new Error('Not authenticated')
        }
        set({ loading: true, error: null })
        try {
          await api.post(
            '/api/auth/change-password',
            { current_password: currentPassword, new_password: newPassword },
            { headers: { Authorization: `Bearer ${accessToken}` } },
          )
          // Clear the force-change flag locally so the banner disappears
          set({
            user: { ...user, must_change_password: false },
            loading: false,
          })
        } catch (err: any) {
          const message =
            err?.response?.data?.detail || 'Could not change password. Try again.'
          set({ error: message, loading: false })
          throw new Error(message)
        }
      },

      logout: () => {
        const token = get().accessToken || localStorage.getItem('av-access-token')
        // Best-effort server-side logout
        if (token) {
          api
            .post('/api/auth/logout', null, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => {})
        }
        localStorage.removeItem('av-access-token')
        set({
          user: null,
          isAuthenticated: false,
          accessToken: null,
          mfaToken: null,
          error: null,
        })
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'av-auth',
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        mfaToken: state.mfaToken,
      }),
    },
  ),
)
